<template>
  <div :class="containerClass">
    <a
      v-if="isCam"
      :class="[
        actionButtonClass,
        'card-background-color text-darkest-color',
      ]"
      href="javascript:void(0);"
      @click="$emit('show-image')"
    >
      <font-awesome-icon
        :class="iconClass"
        :icon="['fas', 'search-plus']"
      />
    </a>
    <a
      v-if="isCam"
      v-tooltip="getTooltipOptions('video')"
      :class="[
        actionButtonClass,
        allAccessButtonClass,
      ]"
      href="javascript:void(0);"
      @click="showVideo"
    >
      <font-awesome-icon
        :class="iconClass"
        :icon="['fas', 'video']"
      />
    </a>
    <a
      v-tooltip="getTooltipOptions('favorite')"
      :class="[
        actionButtonClass,
        allAccessButtonClass,
      ]"
      href="javascript:void(0);"
      @click="toggleFavorite()"
    >
      <font-awesome-icon
        :class="iconClass"
        :icon="[
          isFavoriteImage ? 'fas' : 'far',
          'star',
        ]"
      />
    </a>
  </div>
</template>

<script>
/* eslint camelcase: off */
import { mapActions, mapState } from 'pinia';
import { useUserFavoritesStore } from '@@/stores/UserFavorites';
import { useUserStore } from '@@/stores/User';
import { getStartFreeTrialUrl } from '@@/utils/LoginUtils';

export default {
  name: 'ImageControls',

  props: {
    image: {
      type: Object,
      required: true,
    },
    imageType: {
      type: String,
      required: true,
      validator(value) {
        return ['cam', 'trail_map'].includes(value);
      },
    },
    location: {
      type: Object,
      default: null,
    },
  },

  emits: ['show-image', 'show-video'],

  computed: {
    ...mapState(useUserStore, ['isAllAccess', 'isGuest']),

    ...mapState(useUserFavoritesStore, [
      'getFavoriteListsForCam',
      'getFavoriteListsForTrailMap',
      'isFavoriteCam',
      'isFavoriteTrailMap',
    ]),

    actionButtonClass() {
      return [
        'tw-h-8 tw-w-8',
        'tw-rounded-full',
        'tw-text-center',
      ];
    },

    allAccessButtonClass() {
      return this.isCam && !this.isAllAccess
        ? 'tw-text-white all-access-background-color'
        : 'card-background-color text-darkest-color';
    },

    containerClass() {
      return [
        'tw-absolute tw-top-2.5 tw-right-2.5 tw-z-10',
        'tw-flex tw-flex-col tw-justify-evenly',
        `tw-w-8 ${this.isCam ? 'tw-h-32' : 'tw-h-16'}`,
      ];
    },

    iconClass() {
      return 'tw-text-base tw-align-bottom';
    },

    isCam() {
      return this.imageType === 'cam';
    },

    /**
     * Note that the parent page will dispatch actions to fetch all favorite cams or trail maps
     * so that this action isn't dispatched multiple times by each <ImageView> component since
     * there may be _numerous_ <ImageView> components rendered on a page!
     */
    isFavoriteImage() {
      if (this.isCam) {
        return this.isFavoriteCam({ camId: this.image.id });
      }

      return this.isFavoriteTrailMap({ trailMapId: this.image.id });
    },

    isTrailMap() {
      return this.imageType === 'trail_map';
    },
  },

  methods: {
    ...mapActions(useUserFavoritesStore, [
      'showSelectFavoriteListDialog',
    ]),

    getTooltipOptions(type) {
      if (this.isAllAccess) {
        return null;
      }

      if (this.isTrailMap) {
        return null;
      }

      return {
        content: type === 'video'
          ? 'Timelapse cam videos are are for All-Access members only'
          : 'Favorting cams is for All-Access members only',
        triggers: ['hover'],
      };
    },

    showVideo() {
      if (!this.isAllAccess) {
        this.startFreeTrial();
        return;
      }

      this.$emit('show-video');
    },

    startFreeTrial() {
      const { isGuest } = this;
      const query = { return_to: this.$route.fullPath };
      const url = getStartFreeTrialUrl({ isGuest, query });
      this.$router.push(url);
    },

    async toggleFavorite() {
      if ((!this.isAllAccess && this.isCam) || this.isGuest) {
        this.startFreeTrial();
        return;
      }

      const { location } = this;
      const action = this.isFavoriteImage ? 'update' : 'add';
      const cam = this.isCam ? this.image : null;
      const trailMap = this.isTrailMap ? this.image : null;

      const defaultSelectedLists = this.isCam
        ? this.getFavoriteListsForCam(cam)
        : this.getFavoriteListsForTrailMap(trailMap);

      this.showSelectFavoriteListDialog({
        action,
        cam,
        defaultSelectedLists,
        location,
        trailMap,
      });
    },
  },
};
</script>
